import React from 'react';
import { Padding } from '@extensions/AttributePanel/components/attributes//Padding';
import { BackgroundColor } from '@extensions/AttributePanel/components/attributes/BackgroundColor';
import { BorderWidth } from '../../attributes/BorderWidth';
import { BorderStyle } from '../../attributes/BorderStyle';
import { BorderColor } from '../../attributes/BorderColor';
import { ImageUploaderField, SelectField } from '@extensions/components/Form';
import { Collapse, Grid } from '@arco-design/web-react';
import { TextStyle, useFocusIdx, useEditorProps } from '@bay-email/editor';
import { ClassName } from '../../attributes/ClassName';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';
import { t } from '@bay-email/core';
import { BorderWidthControl } from '../shared/BorderWidthControl';
import { BorderRadiusControl } from '../shared/BorderRadiusControl';

const backgroundRepeatOptions = [
	{
		value: 'no-repeat',
		get label() {
			return t('Show image once');
		},
	},
	{
		value: 'repeat',
		get label() {
			return t('Tile as a pattern');
		},
	},
];

const backgroundSizeOptions = [
	{
		value: 'auto',
		get label() {
			return t('Original Size');
		},
	},
	{
		value: 'cover',
		get label() {
			return t('Fill entire space (may crop edges)');
		},
	},
	{
		value: 'contain',
		get label() {
			return t('Fit entire image (may leave borders)');
		},
	},
];

/**
 * `listingSettings` is the Listing Wrapper: the same wrapper panel, with the
 * listing picker added above the styling controls.
 */
export function Wrapper({ listingSettings }: { listingSettings?: React.ReactNode } = {}) {
	const { focusIdx } = useFocusIdx();
	const { onUploadImage } = useEditorProps();

	return (
		<CollapseWrapper
			isChevron
			iconPosition='right'
			defaultActiveKey={listingSettings ? ['-1', '0', '1', '2'] : ['0', '1', '2']}
			style={{ padding: 0 }}
		>
			{listingSettings && (
				<Collapse.Item
					name='-1'
					header={t('Listing Settings')}
					contentStyle={{ padding: 0 }}
				>
					{listingSettings}
				</Collapse.Item>
			)}
			<Collapse.Item
				name='0'
				header={t('Content Block Settings')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<ImageUploaderField
						labelHidden
						headerTitle={t('Background image upload')}
						name={`${focusIdx}.attributes.background-url`}
						uploadHandler={onUploadImage}
						context='image-src'
						isImageCropperAllowed={true}
						showLink={false}
					/>
					<Grid.Row gutter={16}>
						<Grid.Col span={24}>
							<SelectField
								label={t('Background Repeat')}
								name={`${focusIdx}.attributes.background-repeat`}
								options={backgroundRepeatOptions}
								className='custom-select-right-caret'
							/>
						</Grid.Col>
						<Grid.Col span={24}>
							<SelectField
								label={t('Background size')}
								name={`${focusIdx}.attributes.background-size`}
								options={backgroundSizeOptions}
								className='custom-select-right-caret'
							/>
						</Grid.Col>
					</Grid.Row>
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<BackgroundColor />
						</Grid.Col>
					</Grid.Row>
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='1'
				header={t('Dimension')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3 pt-4'>
					<Padding />
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='2'
				header={t('Styling')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3 pt-4'>
					<TextStyle
						variation='strong'
						style={{ marginBottom: '12px', display: 'block' }}
					>
						{t('Border')}
					</TextStyle>
					<Grid.Row gutter={16}>
						<Grid.Col span={16}>
							<BorderColor secondary />
						</Grid.Col>
						<Grid.Col span={8}>
							<BorderStyle secondary />
						</Grid.Col>
					</Grid.Row>
					<BorderWidthControl />
					<BorderRadiusControl />
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='4'
				header={t('Extra')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<ClassName />
				</div>
			</Collapse.Item>
		</CollapseWrapper>
	);
}
